/**
 * A small, pure ledger for first-try grading.
 *
 * One beat can contribute exactly one verdict. Re-renders, retries and replayed
 * widget messages are therefore harmless. A skip/reveal is recorded as
 * "unknown": it is visible in the result, but is not turned into a miss.
 */

export const FIRST_VERDICT = Object.freeze({
  CORRECT: "correct",
  INCORRECT: "incorrect",
  UNKNOWN: "unknown",
});

export function normalizeFirstVerdict(verdict) {
  if (verdict === true) return FIRST_VERDICT.CORRECT;
  if (verdict === false) return FIRST_VERDICT.INCORRECT;
  if (verdict === null) return FIRST_VERDICT.UNKNOWN;
  const value = String(verdict || "").trim().toLowerCase();
  if (value === "correct" || value === "right" || value === "pass") {
    return FIRST_VERDICT.CORRECT;
  }
  if (value === "incorrect" || value === "wrong" || value === "miss") {
    return FIRST_VERDICT.INCORRECT;
  }
  if (value === "unknown" || value === "skip" || value === "skipped" ||
      value === "reveal" || value === "revealed") {
    return FIRST_VERDICT.UNKNOWN;
  }
  return null;
}

export class FirstAttemptLedger {
  constructor(entries) {
    this._verdicts = new Map();
    if (entries instanceof FirstAttemptLedger) entries = entries.entries();
    if (entries instanceof Map) entries = entries.entries();
    if (Array.isArray(entries) || (entries && typeof entries[Symbol.iterator] === "function")) {
      for (const entry of entries) {
        if (Array.isArray(entry) && entry.length >= 2) this.record(entry[0], entry[1]);
      }
    } else if (entries && typeof entries === "object") {
      for (const [beatId, verdict] of Object.entries(entries)) this.record(beatId, verdict);
    }
  }

  record(beatId, verdict) {
    if (beatId === null || beatId === undefined || String(beatId) === "") return false;
    const key = String(beatId);
    const normalized = normalizeFirstVerdict(verdict);
    if (!normalized || this._verdicts.has(key)) return false;
    this._verdicts.set(key, normalized);
    return true;
  }

  correct(beatId) { return this.record(beatId, FIRST_VERDICT.CORRECT); }
  miss(beatId) { return this.record(beatId, FIRST_VERDICT.INCORRECT); }
  unknown(beatId) { return this.record(beatId, FIRST_VERDICT.UNKNOWN); }
  skip(beatId) { return this.unknown(beatId); }
  reveal(beatId) { return this.unknown(beatId); }
  exclude(beatId, _reason = "") { return this.unknown(beatId); }
  has(beatId) { return this._verdicts.has(String(beatId)); }
  verdict(beatId) { return this._verdicts.get(String(beatId)) || null; }
  entries() { return Array.from(this._verdicts.entries()); }

  summary(expectedCount = this._verdicts.size) {
    let correct = 0, misses = 0, unknown = 0;
    for (const verdict of this._verdicts.values()) {
      if (verdict === FIRST_VERDICT.CORRECT) correct++;
      else if (verdict === FIRST_VERDICT.INCORRECT) misses++;
      else unknown++;
    }
    const attempts = correct + misses;
    const expected = Math.max(this._verdicts.size,
      Number.isFinite(Number(expectedCount)) ? Math.floor(Number(expectedCount)) : 0);
    unknown += Math.max(0, expected - this._verdicts.size);
    return {
      correct,
      attempts,
      misses,
      unknown,
      total: expected,
      observed: this._verdicts.size,
    };
  }
  snapshot(expectedCount) { return this.summary(expectedCount); }

  get size() { return this._verdicts.size; }
  get attempts() { return this.summary().attempts; }
  get misses() { return this.summary().misses; }
  get unknowns() { return this.summary().unknown; }
}

export default FirstAttemptLedger;